import { removeEquationDelimiters, wrappedMathRegex } from '@portal-core/ui/equation/util/mathjax';
import { MathMLToLaTeX } from 'mathml-to-latex';
import { ProseMirrorNode } from 'prosemirror-model';

/**
 * Whether an equation node view's DOM is currently in MathLive edit mode. EquationNodeView
 * removes its dom's contenteditable="false" attribute for the duration of an edit (see
 * EquationNodeView.enterEditMode()/exitEditMode()) — this is the only externally-visible signal
 * of edit state for consumers (e.g. the equation toolbar popup) that don't hold a reference to
 * the node view instance itself, only its DOM.
 */
export function isEquationInEditMode(dom: Element | null): boolean {
  return dom != null && !dom.hasAttribute('contenteditable');
}

/**
 * Pulls the equation's content out as a delimiter-free LaTeX or raw-MathML string, whichever it
 * actually is — a `madcapequation` wrapper's `math`/`mmlmath` child (raw MathML), a bare
 * `math`/`mmlmath` node, or the wrapper's own text content (LaTeX). Exported (not just used by
 * EquationNodeView.syncNode()) so isEditableEquationNode() below and callers like
 * EquationToolbarPopupPluginView can classify a node's content without needing a live node view
 * instance.
 */
export function extractEquationLatex(node: ProseMirrorNode): string {
  let equation = '';

  if (node.type.name === 'math' || node.type.name === 'mmlmath') {
    equation = node.attrs.content ?? '';
  } else {
    const firstChild = node.firstChild;
    if (firstChild?.type.name === 'math' || firstChild?.type.name === 'mmlmath') {
      equation = firstChild.attrs.content ?? '';
    } else if (firstChild?.isText) {
      // Use the wrapper's own textContent (concatenates ALL children), not firstChild's —
      // a tracked-change mark on part of the LaTeX (e.g. an insertion) splits it into multiple
      // sibling text nodes, since ProseMirror can't merge text runs with different marks.
      // Reading only firstChild.textContent silently truncated at the first mark boundary.
      equation = node.textContent;
    }
  }

  return removeEquationDelimiters(equation.trim());
}

/**
 * PBI #186777: MathML equations are read-only, matching pre-MathLive Flare behavior — MathLive's
 * LaTeX-based editing round-trip isn't lossless for raw MathML. Checked by content shape
 * (wrappedMathRegex), not node type, since a MadCap:equation wrapper can contain a math/mmlmath
 * child directly. Shared by EquationNodeView.enterEditMode() (blocks the edit outright) and
 * EquationToolbarPopupPluginView (hides the Edit button so it doesn't invite a click that's
 * always going to be blocked).
 */
export function isEditableEquationNode(node: ProseMirrorNode): boolean {
  return !wrappedMathRegex.test(extractEquationLatex(node));
}

const equationNodeTypeNames = new Set(['madcapequation', 'math', 'mmlmath']);

/**
 * Whether a node is one of the equation-bearing node types — a `madcapequation` wrapper, or a bare
 * `math`/`mmlmath` node with no wrapper (see madcap-equation-schema-plugin.ts's doc comment on the
 * `math` node: a bare `<math>` tag is treated like a `<MadCap:equation>` tag either way). Shared by
 * EquationToolbarPopupPluginView and FlareTextEditorComponent's context menu so both agree on when
 * to show their equation Edit/Delete controls.
 */
export function isEquationNode(node: ProseMirrorNode | null | undefined): boolean {
  return node != null && equationNodeTypeNames.has(node.type.name);
}

/**
 * Converts a MathML equation node's content to a LaTeX string via the mathml-to-latex package
 * (https://github.com/asnunes/mathml-to-latex), for the "Convert to LaTeX" command offered on a
 * non-editable MathML equation (see isEditableEquationNode()'s doc comment) — MathLive can only
 * ever edit LaTeX, so this is the only way to make such an equation editable. The caller is
 * responsible for building the replacement madcapequation node and dispatching the transaction
 * (see convertMathMlEquationToLatex() in equation.command.ts) — this only does the string
 * conversion, which doesn't need a live document position.
 */
export function convertMathMlToLatex(node: ProseMirrorNode): string {
  return MathMLToLaTeX.convert(extractEquationLatex(node));
}

/**
 * Elements/attributes mathml-to-latex@1.8.0 either drops entirely or maps incorrectly, verified
 * empirically (no error/warning of its own is ever raised — it always returns a string):
 * - `<ms>`, `<mglyph>`, Content MathML (`<ci>`, `<cn>`, `<apply>`, `<csymbol>`), and any
 *   unrecognized/custom element all convert to an empty string, silently dropping their content.
 * - The elementary-math family (`<mstack>`, `<mlongdiv>`, `<msrow>`, `<mscarries>`, `<mscarry>`,
 *   `<msline>`) and `<maligngroup>`/`<malignmark>`/`<mlabeledtr>` (table alignment/row-labeling)
 *   produce a plausible-looking but structurally wrong or meaningless result.
 * - `<semantics>`/`<annotation>` drop the annotation and keep only the presentation MathML —
 *   usually fine, since the annotation is normally redundant with it.
 * - `<menclose notation="circle">` is mapped to `\boxed{...}` — a real bug (wrong shape), not just
 *   an unsupported one.
 * - `mathcolor`, `mathbackground`, and `href` attributes are always dropped, on any element.
 */
const LOSSY_MATHML_PATTERN = /<(?:[a-z][\w.-]*:)?(ms|mglyph|mstack|mlongdiv|msrow|mscarries|mscarry|msline|maligngroup|malignmark|mlabeledtr|semantics|annotation|ci|cn|apply|csymbol)[\s/>]|\bmathcolor\s*=|\bmathbackground\s*=|\bhref\s*=|notation\s*=\s*["']circle["']/i;

/**
 * Whether convertMathMlToLatex() failed outright — a non-empty MathML source converted to an
 * empty/whitespace result, which would leave the equation empty rather than just missing some
 * formatting. This happens for an element mathml-to-latex@1.8.0 doesn't recognize at all (`<ms>`,
 * `<mglyph>`, Content MathML, any custom element) or malformed XML — see LOSSY_MATHML_PATTERN's
 * doc comment. Checked separately from, and takes priority over, isLikelyLossyMathMlConversion()
 * below: losing all content is a harder failure than losing some formatting, so callers should
 * abort the conversion (leaving the original equation untouched) and report it with the more
 * severe error styling rather than the softer lossy-conversion warning.
 */
export function isFailedMathMlConversion(mathml: string, latex: string): boolean {
  return !!mathml.trim() && !latex.trim();
}

/** Shown by both the equation toolbar popup and the context menu when isFailedMathMlConversion() flags a conversion and the conversion is aborted. */
export const FAILED_MATHML_CONVERSION_ERROR = 'Equation could not be converted to LaTeX. No changes were made.';

/**
 * Best-effort check for whether convertMathMlToLatex()'s result likely dropped content or
 * formatting from the source MathML while still producing a usable result — see
 * LOSSY_MATHML_PATTERN's doc comment for the specific gaps this is inferred from, since the
 * library itself never signals a partial/failed conversion. Callers should check
 * isFailedMathMlConversion() first — a source that fails outright often also matches this pattern
 * (e.g. `<ms>`), but the failure is the more actionable signal to report.
 */
export function isLikelyLossyMathMlConversion(mathml: string): boolean {
  return LOSSY_MATHML_PATTERN.test(mathml);
}

/** Shown by both the equation toolbar popup and the context menu when isLikelyLossyMathMlConversion() flags a conversion. */
export const LOSSY_MATHML_CONVERSION_WARNING = 'Equation may have lost some content or formatting when converting to LaTeX. Please review it before continuing.';

/**
 * Result of tryConvertMathMlToLatex(): either the conversion failed outright ('failed', see
 * isFailedMathMlConversion()), it succeeded but may have lost content/formatting ('lossy', see
 * isLikelyLossyMathMlConversion()), or it succeeded cleanly ('ok'). `latex` is only present when
 * the caller should proceed with the conversion (both 'lossy' and 'ok').
 */
export type MathMlToLatexConversionResult = { status: 'failed' } | { status: 'lossy' | 'ok'; latex: string };

/**
 * Runs the full "Convert to LaTeX" check sequence for a MathML equation node — extract, convert,
 * then classify the result via isFailedMathMlConversion()/isLikelyLossyMathMlConversion() — shared
 * by the two entry points that offer the command (the equation toolbar popup and the flare text
 * editor's context menu) so their control flow can't drift from each other.
 */
export function tryConvertMathMlToLatex(node: ProseMirrorNode): MathMlToLatexConversionResult {
  const mathml = extractEquationLatex(node);
  const latex = convertMathMlToLatex(node);

  if (isFailedMathMlConversion(mathml, latex)) {
    return { status: 'failed' };
  }

  return { status: isLikelyLossyMathMlConversion(mathml) ? 'lossy' : 'ok', latex };
}
